import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { X, Check, Loader2, CreditCard } from 'lucide-react';
import { API } from '../App';
import BrandLogo from './BrandLogo';

const UpgradeModal = ({ isOpen, onClose, qrCodeId, carNickname, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [pricing, setPricing] = useState({
    basePricePaise: 34900,
    finalPricePaise: 34900,
    offerApplied: false,
    durationDays: 365,
    offerScope: 'global'
  });

  useEffect(() => {
    const loadPricing = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${API}/settings/user`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setPricing({
          basePricePaise: response.data.subscription_price_paise,
          finalPricePaise: response.data.final_price_paise,
          offerApplied: response.data.offer_applied,
          durationDays: response.data.subscription_duration_days,
          offerScope: response.data.offer_scope || 'global'
        });
      } catch (error) {
        // Keep defaults
      }
    };

    if (isOpen) {
      loadPricing();
    }
  }, [isOpen]);

  const formatPrice = (paise) => Math.round(paise / 100);
  const durationLabel = pricing.durationDays >= 365
    ? `${Math.round(pricing.durationDays / 365)} full year${pricing.durationDays >= 730 ? 's' : ''}`
    : `${pricing.durationDays} days`;
  const offerLabel = pricing.offerScope === 'new_users' ? 'New User Offer' : 'Limited Offer';

  const handlePayment = async () => {
    setLoading(true);
    try {
      // Create order
      const orderResponse = await axios.post(`${API}/payment/create-order`, {
        qr_code_id: qrCodeId
      });

      const { order_id, amount, key_id } = orderResponse.data;

      // Load Razorpay script if not loaded
      if (!window.Razorpay) {
        const script = document.createElement('script');
        script.src = 'https://checkout.razorpay.com/v1/checkout.js';
        script.async = true;
        document.body.appendChild(script);
        await new Promise(resolve => script.onload = resolve);
      }

      // Open Razorpay checkout
      const options = {
        key: key_id,
        amount: amount,
        currency: 'INR',
        name: 'sideo',
        description: `QR Code Subscription - ${carNickname || 'Vehicle'}`,
        order_id: order_id,
        handler: async (response) => {
          try {
            // Verify payment
            await axios.post(`${API}/payment/verify`, {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature
            });
            
            toast.success('Payment successful! Your QR code is now active.');
            if (window.fbq) window.fbq('track', 'Purchase', { value: Math.round(amount / 100), currency: 'INR' });
            onSuccess?.();
            onClose();
          } catch (error) {
            toast.error('Payment verification failed. Please contact support.');
            setLoading(false);
          }
        },
        prefill: {
          name: '',
          email: '',
          contact: ''
        },
        theme: {
          color: '#1e40af'
        },
        modal: {
          ondismiss: () => {
            setLoading(false);
          }
        }
      };

      const razorpay = new window.Razorpay(options);
      razorpay.open();
      
    } catch (error) {
      console.error('Payment error:', error);
      toast.error(error.response?.data?.detail || 'Failed to initiate payment');
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-md w-full shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-slate-200">
          <div className="flex items-center space-x-3">
            <div className="w-14 h-14 rounded-full bg-blue-100 flex items-center justify-center">
              <BrandLogo className="h-12 w-auto" />
            </div>
            <h2 className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Barlow' }}>
              Renew QR Code
            </h2>
          </div>
          {pricing.offerApplied && (
            <span className="px-3 py-1 rounded-full bg-emerald-100 text-emerald-700 text-xs font-bold">
              {offerLabel}
            </span>
          )}
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-full hover:bg-slate-100 flex items-center justify-center"
          >
            <X className="w-5 h-5 text-slate-500" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          <div className="text-center mb-6">
            <div className="text-4xl font-bold text-slate-900 mb-1">
              {pricing.offerApplied && (
                <span className="text-lg font-normal text-slate-400 line-through mr-2">₹{formatPrice(pricing.basePricePaise)}</span>
              )}
              ₹{formatPrice(pricing.finalPricePaise)}<span className="text-lg font-normal text-slate-500">/year</span>
            </div>
            <p className="text-slate-600">per QR renewal</p>
          </div>

          <div className="space-y-3 mb-6">
            <div className="flex items-center space-x-3">
              <div className="w-5 h-5 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
                <Check className="w-3 h-3 text-emerald-600" />
              </div>
              <span className="text-slate-700">Unlimited messages from scanners</span>
            </div>
            <div className="flex items-center space-x-3">
              <div className="w-5 h-5 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
                <Check className="w-3 h-3 text-emerald-600" />
              </div>
              <span className="text-slate-700">Valid for {durationLabel}</span>
            </div>
            <div className="flex items-center space-x-3">
              <div className="w-5 h-5 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
                <Check className="w-3 h-3 text-emerald-600" />
              </div>
              <span className="text-slate-700">Instant SMS notifications</span>
            </div>
            <div className="flex items-center space-x-3">
              <div className="w-5 h-5 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
                <Check className="w-3 h-3 text-emerald-600" />
              </div>
              <span className="text-slate-700">Privacy protected - number never shared</span>
            </div>
          </div>

          <button
            onClick={handlePayment}
            disabled={loading}
            className="w-full h-12 bg-[#0DB504] hover:bg-[#0a9e03] text-white rounded-xl font-semibold flex items-center justify-center space-x-2 transition-colors disabled:opacity-50"
            data-testid="pay-now-btn"
          >
            {loading ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <>
                <CreditCard className="w-5 h-5" />
                <span>Renew ₹{formatPrice(pricing.finalPricePaise)}</span>
              </>
            )}
          </button>

          <p className="text-xs text-slate-500 text-center mt-4">
            Secure payment powered by Razorpay
          </p>
        </div>
      </div>
    </div>
  );
};

export default UpgradeModal;
